import AttachmentDataProviderItemType from 'owa-service/lib/contract/AttachmentDataProviderItemType';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentThumbnailInfo from 'owa-service/lib/contract/AttachmentThumbnailInfo';
import attachmentThumbnailInfo from 'owa-service/lib/factory/attachmentThumbnailInfo';
import FacebookDataProviderItem from 'owa-service/lib/contract/FacebookDataProviderItem';
import FacebookImageScopeType from 'owa-service/lib/contract/FacebookImageScopeType';
import FacebookProviderScope from 'owa-service/lib/contract/FacebookProviderScope';
import GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import handleGetItemsResponse from './handleGetItemsResponse';
import { assertNever } from 'owa-assert';
import { FacebookDataProviderItemType } from '../constants';
import {
    FacebookPhotoItem,
    FacebookPhotoType,
    FacebookImage,
    getFacebookPhotos,
} from 'owa-fileprovider-services';
import {
    createAttachmentDataProviderItemsResponse,
    createFailedAttachmentDataProviderItemsResponse,
} from './createAttachmentDataProviderItemsResponse';

const MINIMUM_HEIGHT_FOR_THUMBNAIL = 150;
const MINIMUM_WIDTH_FOR_THUMBNAIL = 200;

function getAcceptableImage(images: FacebookImage[]): FacebookImage {
    // The images at the end would be the smallest and the ones at the beginning would be of the highest
    // quality. So start from the bottom. Also copy the array using the spread operator so that we don't
    // manipulate the original array.
    const [acceptableImage] = [...images]
        .reverse()
        .filter(
            image =>
                image.width >= MINIMUM_WIDTH_FOR_THUMBNAIL &&
                image.height >= MINIMUM_HEIGHT_FOR_THUMBNAIL
        );

    // If acceptable image is found then return otherwise
    // return the first image
    return acceptableImage || images[0];
}

function getThumbnail(item: FacebookPhotoItem): AttachmentThumbnailInfo {
    const acceptableImage = getAcceptableImage(item.images);

    return attachmentThumbnailInfo({
        Url: acceptableImage.source,
        Height: acceptableImage.height,
        Width: acceptableImage.width,
    });
}

function createAttachmentDataProviderItem(item: FacebookPhotoItem): FacebookDataProviderItem {
    const thumbnailImage = getThumbnail(item);
    const mainImage = item.images[0];
    const previewImage = attachmentThumbnailInfo({
        Url: mainImage.source,
        Height: mainImage.height,
        Width: mainImage.width,
    });

    return {
        __type: FacebookDataProviderItemType,
        Id: item.id,
        Name: `Photo ${item.id}.jpg`,
        AttachmentProviderId: null,
        ProviderType: AttachmentDataProviderType.Facebook,
        Location: mainImage.source,
        DownloadUrl: `${mainImage.source}&dl=1`,
        Thumbnail: thumbnailImage,
        Preview: previewImage,
        From: item.from ? item.from.name : null,
        PlaceName: item.place ? item.place.name : null,
        AlbumName: item.album ? item.album.name : null,
        CreatedTime: item.created_time || null,
        PostUrl: item.link,
        Type: AttachmentDataProviderItemType.File,
    };
}

const createResponseFunc = (items: FacebookPhotoItem[]) =>
    createAttachmentDataProviderItemsResponse(items, createAttachmentDataProviderItem);

function getPhotoTypeForImageScopeType(scopeType: FacebookImageScopeType) {
    switch (scopeType) {
        case FacebookImageScopeType.Uploaded:
            return FacebookPhotoType.Uploaded;
        case FacebookImageScopeType.Tagged:
            return FacebookPhotoType.Tagged;
        default:
            return assertNever(scopeType);
    }
}

function getPhotoTypeFromScope(scope: FacebookProviderScope | null) {
    if (scope) {
        return getPhotoTypeForImageScopeType(scope.Type);
    }

    return FacebookPhotoType.Tagged;
}

export default async function getFacebookFileProviderItems(
    scope: FacebookProviderScope
): Promise<GetAttachmentDataProviderItemsResponse> {
    const photoType = getPhotoTypeFromScope(scope);
    const { errorResponse, result } = await getFacebookPhotos(photoType);

    if (result) {
        return handleGetItemsResponse(
            result,
            null /* errorResponse */,
            createResponseFunc,
            createFailedAttachmentDataProviderItemsResponse
        );
    } else {
        return handleGetItemsResponse(
            null /* result */,
            errorResponse,
            createResponseFunc,
            createFailedAttachmentDataProviderItemsResponse
        );
    }
}
